import { debounce } from "es-toolkit";
import { on } from "svelte/events";
import {
  createCachedFacetOrder,
  createCachedPriceMax,
  createCachedPriceMin,
} from "~/lib/bloomreach/cache.svelte";
import { mapFacetsToFilters, mapProductsToCards } from "~/lib/bloomreach/transformers";
import type { BloomreachResponse } from "~/lib/bloomreach/types";
import type { FilterState } from "~/lib/filters/types";
import { migrateLegacyUrl } from "~/lib/legacy-url-migration";
import { getTopmostCardIndex, scrollToCard } from "~/lib/scroll-utils";
import { parseListUrlState } from "~/lib/url-state";

const MAX_INITIAL_ROWS = 200;

// Minimum query shape the composable depends on. Structural — compatible with
// the real TanStack CreateInfiniteQueryResult and with test mocks.
export interface ListingQuery {
  data:
    | {
        pages: Array<BloomreachResponse & { facet_counts?: BloomreachResponse["facet_counts"] }>;
      }
    | undefined;
  error: unknown;
  isPending: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  isPlaceholderData: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => Promise<{ data?: { pages: BloomreachResponse[] } }>;
}

export interface ListingStateConfig<Q extends ListingQuery> {
  cardSelector: string;
  sessionKey: string;
  resolvedRows: () => number;
  makeQuery: (opts: { filters: () => FilterState; sort: () => string; initialRows: number }) => Q;
  onScrollStop?: (filters: FilterState, sort: string, start: number) => void;
}

export function createListingState<Q extends ListingQuery>(config: ListingStateConfig<Q>) {
  // If the user has landed with a legacy filtered URL (?fq=/page=/etc parameters),
  // swap them to the new format before we continue.
  migrateLegacyUrl(config.resolvedRows());

  const { filters: initialFilters, sort: initialSort, start: initialStart } = parseListUrlState();

  const storedCardIndex = parseInt(sessionStorage.getItem(config.sessionKey) ?? "", 10);
  const scrollTarget = !isNaN(storedCardIndex) ? storedCardIndex : initialStart;

  const initialRows =
    scrollTarget > 0
      ? Math.min(scrollTarget + config.resolvedRows(), MAX_INITIAL_ROWS)
      : config.resolvedRows();

  let pendingFilters: FilterState = $state(initialFilters);
  let _debouncedFilters: FilterState = $state(initialFilters);
  const _debouncedUpdate = debounce((v: FilterState) => {
    _debouncedFilters = v;
  }, 300);
  $effect(() => {
    _debouncedUpdate(pendingFilters);
    return () => _debouncedUpdate.cancel();
  });
  const activeFilters = {
    get current() {
      return _debouncedFilters;
    },
    setImmediately(v: FilterState) {
      _debouncedUpdate.cancel();
      _debouncedFilters = v;
    },
  };
  let activeSort: string = $state(initialSort);

  // Incremented by resetAll so that trackListingEvents can gate the
  // post-reset filtersChanged emission without coupling to this module's internals.
  let resetEpoch: number = $state(0);

  const query = config.makeQuery({
    filters: () => activeFilters.current,
    sort: () => activeSort,
    initialRows,
  });

  const allDocs = $derived(query.data?.pages.flatMap((p) => p.response.docs) ?? []);

  // Restore scroll to the card the user was viewing. The flag is set before the
  // rAF call to prevent a second restoration if reactive deps change mid-flight.
  let scrollRestored = scrollTarget <= 0;
  $effect(() => {
    if (scrollRestored || !query.data || query.isFetching) return;
    const fetched = allDocs.length;
    if (fetched <= scrollTarget && query.hasNextPage) {
      query.fetchNextPage();
      return;
    }
    scrollRestored = true;
    scrollToCard(config.cardSelector, scrollTarget);
  });

  // Track the topmost visible card on scroll. Updates sessionStorage for
  // fine-grained restoration and keeps the URL ?start param in sync via
  // onScrollStop when provided.
  //
  // Uses a plain passive listener + debounce rather than runed's ScrollState,
  // which runs setArrivedState on every animation frame (reading six layout
  // properties per frame) even when nothing is scrolling — causing forced
  // reflows whenever Svelte is writing to the DOM.
  $effect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    function onScrollStop() {
      if (!scrollRestored) return;
      const cardIndex = getTopmostCardIndex(config.cardSelector);
      sessionStorage.setItem(config.sessionKey, String(cardIndex));
      const pageStart = Math.floor(cardIndex / config.resolvedRows()) * config.resolvedRows();
      config.onScrollStop?.(activeFilters.current, activeSort, pageStart);
    }

    function handleScroll() {
      clearTimeout(timeout);
      timeout = setTimeout(onScrollStop, 200);
    }

    const off = on(window, "scroll", handleScroll, { passive: true });
    return () => {
      off();
      clearTimeout(timeout);
    };
  });

  const resultCount = $derived(query.data?.pages[0]?.response.numFound);
  const isInitialError = $derived(query.isError && !query.data);
  const isLoadMoreError = $derived(query.isError && !!query.data);

  $effect(() => {
    if (isInitialError) {
      console.error("[Aurora] Listing query failed:", query.error);
    }
  });

  const cachedPriceMax = createCachedPriceMax(
    () => query,
    () => activeFilters.current,
  );

  const cachedPriceMin = createCachedPriceMin(
    () => query,
    () => activeFilters.current,
  );

  const cachedFacetOrder = createCachedFacetOrder(
    () => query,
    () => activeFilters.current,
  );

  const filters = $derived(
    mapFacetsToFilters(
      query.data?.pages[0]?.facet_counts,
      cachedPriceMax.current,
      cachedFacetOrder.current,
      cachedPriceMin.current,
    ),
  );

  const products = $derived(mapProductsToCards(allDocs));

  return {
    // Settable state — getter/setter pairs so Svelte bind: works on member access.
    get pendingFilters(): FilterState {
      return pendingFilters;
    },
    set pendingFilters(v: FilterState) {
      pendingFilters = v;
    },
    get activeSort(): string {
      return activeSort;
    },
    set activeSort(v: string) {
      activeSort = v;
    },
    // Read-only reactive state
    activeFilters,
    query,
    get resetEpoch(): number {
      return resetEpoch;
    },
    // Derived values
    get allDocs() {
      return allDocs;
    },
    get resultCount() {
      return resultCount;
    },
    get isInitialError() {
      return isInitialError;
    },
    get isLoadMoreError() {
      return isLoadMoreError;
    },
    get filters() {
      return filters;
    },
    get products() {
      return products;
    },
    // Actions
    setFiltersImmediately(f: FilterState) {
      activeFilters.setImmediately(f);
    },
    clearScrollSession() {
      sessionStorage.removeItem(config.sessionKey);
    },
    // Returns true if the sort changed, false if it was already active.
    // Callers use the return value to decide whether to emit sort analytics.
    handleSortChange(value: string): boolean {
      if (value === activeSort) return false;
      activeSort = value;
      return true;
    },
    // Returns pagination info for analytics, or null if the fetch returned no data.
    async handleLoadMore(): Promise<{ pageStart: number; loadedCount: number } | null> {
      const result = await query.fetchNextPage();
      if (!result.data) return null;
      const pages = result.data.pages as BloomreachResponse[];
      const lastPage = pages[pages.length - 1];
      return {
        pageStart: lastPage.response.start,
        loadedCount: result.data.pages.flatMap((p) => (p as BloomreachResponse).response.docs)
          .length,
      };
    },
    resetAll() {
      resetEpoch += 1;
      pendingFilters = {};
      activeFilters.setImmediately({});
      activeSort = "";
    },
  };
}

export type ListingState = ReturnType<typeof createListingState>;
